import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import {
  hasSubmittedScheduleConflict,
  isCreatorScheduleConflictValid
} from '@/utils/creatorScheduleConflict';
import { scrollElementToTop } from '@/utils/scrollElementToTop';
import { ProductPhysicalSelectEnum, ProductType } from '../../config';
import { getEstimatedRateLevel } from '../EstimatePriceComponents/rateValidation';
import { isScreeningAnswerValid } from '../ScreeningQuestions/validation';
import { AudienceViewSource, SelfEstimatedStatus } from './config';

/** 同一申请弹窗的下一步和最终提交复用现有校验规则。 */
export default function useTakeOrderValidation({
  isPriceValid,
  setSelectCountryError,
  setAudienceError,
  setScheduleError,
  onInvalid
}: {
  isPriceValid: boolean;
  setSelectCountryError: (error: boolean) => void;
  setAudienceError: (error: boolean) => void;
  setScheduleError: (error: boolean) => void;
  onInvalid?: (step: 1 | 2) => void;
}) {
  return (includeAudience = true) => {
    // 点击时读取最新填写值，避免确认阶段沿用上一步的校验结果。
    const { detailData } = useDetailDataStore.getState();
    const {
      showModalAcceptAhaPicksOffer,
      agreeProductShip,
      takeOrderAudienceView
    } = useWebModalStore.getState();
    const {
      newOrderPrice,
      creatorLowActualGet,
      creatorHighActualGet,
      screeningQuestions,
      creatorScheduleConflict,
      setScreeningQuestionsError,
      triggerPriceErrorShake
    } = useMultiplePricesStore.getState();
    const invalidRate =
      !showModalAcceptAhaPicksOffer &&
      (!isPriceValid ||
        getEstimatedRateLevel(
          newOrderPrice,
          creatorLowActualGet,
          creatorHighActualGet
        ) === 'error');
    const invalidQuestionIndex = screeningQuestions.findIndex(
      (question) => !isScreeningAnswerValid(question)
    );
    const invalidSchedule =
      !hasSubmittedScheduleConflict(detailData?.order?.scheduleConflict) &&
      !isCreatorScheduleConflictValid(creatorScheduleConflict);
    const invalidCountry =
      detailData?.product?.productType === ProductType.PHYSICAL_PRODUCT &&
      detailData?.product?.physicalProductShipType ===
        ProductPhysicalSelectEnum.ship &&
      !agreeProductShip;
    const audienceView = takeOrderAudienceView ?? detailData?.audienceView;
    const invalidAudience =
      includeAudience &&
      !(
        audienceView?.source === AudienceViewSource.FIRST_PARTY ||
        (audienceView?.source === AudienceViewSource.SELF_ESTIMATED &&
          audienceView?.status === SelfEstimatedStatus.SUCCESS)
      );

    setScreeningQuestionsError(invalidQuestionIndex !== -1);
    setScheduleError(invalidSchedule);
    setSelectCountryError(invalidCountry);
    if (includeAudience) setAudienceError(invalidAudience);
    if (invalidRate) triggerPriceErrorShake();

    // 与当前填写区的字段顺序一致；全部标红后只定位第一个错误。
    const target = invalidRate
      ? 'take-order-price-fields'
      : invalidQuestionIndex !== -1
        ? `take-order-screening-question-${invalidQuestionIndex}`
        : invalidSchedule
          ? 'take-order-schedule-conflict'
          : invalidCountry
            ? 'take-order-shipping-country'
            : invalidAudience
              ? 'take-order-audience-demographics'
              : null;
    if (!target) return true;

    onInvalid?.(target === 'take-order-audience-demographics' ? 1 : 2);
    scrollElementToTop(target);
    return false;
  };
}
